import {
  CollectionContext,
  OrganizationDataRecipientDto,
} from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";
import {
  doesGppSignalApplyForVendor,
  doesVendorHaveUsClassification,
  isCaContractFieldRequiredForVendor,
  isGdprInternationalDataTransferComplete,
  isUsClassificationRequiredForVendor,
  isVendorCaContractDocumentationComplete,
  isVendorGdprClassificationComplete,
  isVendorGppSignalDocumentationComplete,
} from "./VendorDocumentationPage";
import { isEmployeeOnlyCollectionContext } from "../vendor-inventory/vendorInventoryUtils";

export type VendorDocumentationProperty =
  | "us-classification"
  | "gdpr-classification"
  | "ca-contract-requirement"
  | "gpp-signal"
  | "international-data-transfer";

export const MISSING_DOC_LABELS: Record<VendorDocumentationProperty, string> = {
  "us-classification": "US Classification",
  "gdpr-classification": "GDPR class",
  "ca-contract-requirement": "CA Contract",
  "gpp-signal": "GPP signal",
  "international-data-transfer": "Intl Data Transfers",
};

const VendorDocumentationEmailTemplates: Record<
  VendorDocumentationProperty,
  { body: string; header: string }
> = {
  "us-classification": {
    header: "US Classification",
    body: `We are trying to determine whether your company is a "processor" or "controller" of personal data it receives from us, as those terms are defined by US data privacy laws. (Also called "service provider" and "third party," under the California Consumer Privacy Act.) So far, we haven't been able to find the documentation to help us make that determination.

Could you please provide assistance by clarifying whether your company acts as a processor or controller, and indicate where I can find the required contractual language to confirm that? If you are a processor but no such language exists, would you be willing to sign a data processing agreement (DPA) with us?`,
  },
  "gdpr-classification": {
    header: "GDPR",
    body: `We are trying to determine whether your company is a "processor" or "controller" of personal data it receives from us, as those terms are defined by the GDPR. So far, we haven't been able to find the documentation to help us make that determination.

Could you please provide assistance by clarifying whether your company acts as a processor or controller, and indicate where I can find the required contractual language to confirm that? If you are a processor but no such language exists, would you be willing to sign a data processing agreement (DPA) with us?`,
  },
  "ca-contract-requirement": {
    header: "California Contract",
    body: `California's privacy law (the CCPA) requires that recipients of personal information (including service providers, contractors, and third parties) must enter into a contract that:
- States that the personal information is being disclosed for limited and specific purposes;
- Requires you to comply with applicable obligations under the CCPA;
- Grants us the right to take reasonable steps to ensure the recipient's compliance;
- Requires that you notify us if it can no longer meet its CCPA obligations; and
- Grants our business the right to take reasonable steps to stop and remediate unauthorized use of personal information.

Could you please provide assistance by clarifying whether your company has a contract covering the requirements above?`,
  },
  "gpp-signal": {
    header: "GPP",
    body: `We are trying to determine whether your company respects Global Privacy Protocol (GPP) signals for opt-outs and online consents. So far, we haven't been able to find the documentation to help us make that determination.

Could you please provide assistance by clarifying whether your company respects GPP signals, and indicate where I can find a contract or other documentation to confirm that?`,
  },
  "international-data-transfer": {
    header: "International Data Transfer",
    body: `You are one of our providers who processes personal data and we are trying to determine if you have Standard Contractual Clauses (SCCs) in place that govern our transfer of personal data to you from the EEA/UK/CH, in accordance with GDPR. If you have SCCs in place, could you please direct me to the supporting documentation?`,
  },
};

export function getMissingVendorDocumentationProperties(
  org: OrganizationDto,
  vendor: OrganizationDataRecipientDto,
  collectionContext: CollectionContext | CollectionContext[] = "CONSUMER",
): VendorDocumentationProperty[] {
  const missing: VendorDocumentationProperty[] = [];
  const isEeaUk =
    !isEmployeeOnlyCollectionContext(vendor.collectionContext ?? []) &&
    org?.featureGdpr &&
    vendor.processingRegions.includes("EEA_UK");

  if (
    isUsClassificationRequiredForVendor(vendor, org, collectionContext) &&
    !doesVendorHaveUsClassification(vendor)
  ) {
    missing.push("us-classification");
  }

  if (
    isCaContractFieldRequiredForVendor(vendor) &&
    !isVendorCaContractDocumentationComplete(vendor)
  ) {
    missing.push("ca-contract-requirement");
  }

  if (isEeaUk) {
    if (!isVendorGdprClassificationComplete(vendor)) {
      missing.push("gdpr-classification");
    }
    if (!isGdprInternationalDataTransferComplete(vendor)) {
      missing.push("international-data-transfer");
    }
  }

  if (doesGppSignalApplyForVendor(vendor) && !isVendorGppSignalDocumentationComplete(vendor)) {
    missing.push("gpp-signal");
  }

  return missing;
}

export function getDefaultEmailBodyForVendor(
  org: OrganizationDto,
  vendor: OrganizationDataRecipientDto,
  collectionContext: CollectionContext | CollectionContext[] = "CONSUMER",
): string {
  const missingVendorProperties = getMissingVendorDocumentationProperties(
    org,
    vendor,
    collectionContext,
  );

  let body = `Hello,

We are currently reviewing all of our vendors for privacy compliance and are contacting you to ask about certain contract requirements under privacy laws.`;

  for (const property of missingVendorProperties) {
    let newText =
      missingVendorProperties.length > 1
        ? VendorDocumentationEmailTemplates[property].header + "\n\n"
        : "";
    newText += VendorDocumentationEmailTemplates[property].body;
    body += "\n\n" + newText;
  }

  return body;
}
